<template>
    <div class="mt-6 content">
        <div class="is-flex is-justify-content-space-between is-align-items-flex-start">
            <div class="columns is-gapless is-mobile mr-6" style="max-width: 80%;">
                <div class="column is-narrow"><i class="fad fa-fw fa-clipboard-list-check has-text-white mr-3 is-size-4"></i></div>
                <div class="column">
                    <h3 class="has-text-white">Study Log</h3>
                    <p class="has-text-white">Your Study Log records the time automatically tracked in the online course along with other qualifying activities. It represents the truthful and accurate account of the activities you completed for this course and is used to determine what certifications you qualify for.</p>
                    <p class="has-text-white">Access lessons for this course from the
                        <router-link :to="{name: 'course-outline'}" class="has-text-white"><strong class="has-text-white" style="white-space: nowrap;"><i class="fad fa-rectangle-list mr-2"></i>Course Outline</strong></router-link>
                        or, if available, record offline time and other qualifying activities to contribute to the log.
                    </p>
                </div>
            </div>
            <b-button type="is-success" class="ml-4" :class="{'is-invisible': isLoading || !canLogTime}" @click="openEntryPanel">
                <i class="fas fa-plus mr-2"></i>Log Activity
            </b-button>
        </div>

        <div class="mt-6 pb-6">
            <study-activity-panel :enrollment-id="Number(enrollmentId)"/>

            <ui-card class="pt-0 pl-4 pr-6 mb-5">
                <!-- Loading State -->
                <div class="has-text-centered py-2" v-if="isLoading">
                    <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading study log...
                </div>

                <template v-else>
                    <!-- Course complete: study log frozen -->
                    <b-message type="is-info" v-if="isReadonly" class="mb-5">
                        <i class="far fa-lock mr-2"></i>
                        This course is complete, so your Study Log is locked. It remains available here as a record of the time and activities you completed.
                    </b-message>

                    <!-- Milestone reflection required -->
                    <b-message type="is-warning" v-if="milestoneLocked && !isReadonly" class="mb-5">
                        <i class="far fa-flag-swallowtail mr-2"></i>
                        You've reached your milestone checkpoint<template v-if="lockedMilestone"> at {{ lockedMilestone.hour_threshold }} hours</template>.
                        Time logging is paused until your reflection has been submitted and approved.
                        <router-link :to="{name: 'enroll-reflections', params: {enrollmentId: enrollmentId}}"><strong>Go to Reflections</strong></router-link>
                    </b-message>

                    <!-- Overall Hours Progress -->
                    <div v-if="maxHours > 0" class="overall-progress-section mb-5">
                        <h5 class="mb-3"><i class="far fa-clock mr-2"></i>Overall Hours Progress</h5>
                        <p class="has-text-grey-dark mb-4">
                            You've logged <strong>{{ formatDuration(totalLoggedHours) }}</strong> of <strong>{{ formatDuration(maxHours) }}</strong>.
                            <span v-if="totalLoggedHours >= maxHours" class="has-text-success">
                                <i class="fas fa-check-circle mr-1"></i>Hour requirement met!
                            </span>
                            <span v-else>
                                {{ formatDuration(maxHours - totalLoggedHours) }} remaining.
                            </span>
                        </p>
                        <progress
                            class="progress mt-4 mb-5"
                            :class="totalLoggedHours >= maxHours ? 'is-success' : 'is-info'"
                            :value="Math.min(totalLoggedHours, maxHours)"
                            :max="maxHours"
                        ></progress>
                        <hr/>
                    </div>

                    <!-- Objectives Progress Section -->
                    <div v-if="objectivesWithProgress.length > 0" class="objectives-section mb-5">
                        <h5 class="mb-4"><i class="far fa-bullseye-arrow mr-2"></i>Course Study Plan Objectives</h5>

                        <div class="objectives-content">
                            <div v-for="(obj, index) in objectivesWithProgress" :key="'obj-'+index" class="objective-row">
                                <div class="columns is-vcentered is-mobile mb-0">
                                    <div class="column">
                                        <div class="objective-title">
                                            {{ obj.text }}
                                            <span v-if="obj.stem" class="tag is-success is-light ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                                        </div>
                                        <div class="hours-display">
                                            <strong>{{ obj.loggedHours }}</strong> of <strong>{{ obj.plannedHours }}</strong> Hours
                                            <span class="has-text-grey">({{ obj.remainingHours }} Remaining)</span>
                                        </div>
                                    </div>
                                    <div class="column is-narrow" v-if="canLogTime">
                                        <b-button
                                            type="is-success"
                                            size="is-small"
                                            @click="openEntryPanel(obj.text)"
                                        >
                                            <i class="fas fa-plus mr-1"></i>Log Activity
                                        </b-button>
                                    </div>
                                </div>
                                <b-progress
                                    :value="obj.loggedHours"
                                    :max="obj.plannedHours || obj.loggedHours || 1"
                                    :type="obj.plannedHours > 0 && obj.loggedHours >= obj.plannedHours ? 'is-success' : 'is-info'"
                                    size="is-small"
                                ></b-progress>
                            </div>
                        </div>

                        <hr/>
                    </div>

                    <!-- Empty State -->
                    <div class="columns is-gapless pt-2" v-if="studyLog.length === 0">
                        <div class="column is-narrow"><i class="far fa-fw fa-clipboard-list-check mr-2"></i></div>
                        <div class="column">
                            <div class="has-text-weight-bold">You haven't logged any study time for this course yet.</div>
                            <div class="mt-1">Time in online lessons is recorded automatically<template v-if="canLogTime">, or click <em>Log Activity</em> to record offline study time</template>.</div>
                        </div>
                    </div>

                    <!-- Study Log Table -->
                <table class="table" v-else>
                    <template v-for="row in studyLog">
                        <tr class="log-row" :key="row.date">
                            <template>
                                <td style="width: 10px;">
                                    <b-tag type="is-success" style="width: 100%; justify-content: left;"><i class="fas fa-check fa-fw mr-2"></i><strong class="has-text-white">{{ row.totalmins }}</strong> Minute<span v-if="row.totalmins !== 1">s</span></b-tag>
                                </td>
                                <td>
                                    <div class="log-title">{{ row.date | moment('dddd, MMMM Do, YYYY') }}</div>

                                    <template v-if="row.entries.length === 1">
                                        <div class="is-size-7 mt-2" v-if="getComponentOrActivity(row.entries[0])">
                                            <i class="far fa-fw fa-location-dot mr-1"></i>{{ getComponentOrActivity(row.entries[0]) }}
                                            <span v-if="isStemEntry(row.entries[0])" class="tag is-success is-light is-size-7 ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                                        </div>
                                        <div class="is-size-7 has-text-grey"><i class="far fa-fw fa-clock mr-1"></i>{{ displayDates(row.start_date, row.end_date) }}</div>
                                    </template>
                                    <template v-else>
                                        <div>
                                            <div class="log-date is-clickable" @click="row.showEntries = !row.showEntries">
                                                <i class="fas fa-fw mr-1" :class="{'fa-chevron-down': row.showEntries, 'fa-chevron-right': !row.showEntries}"></i>
                                                {{ row.entries.length }} Records
                                            </div>

                                            <div class="mb-5" v-if="row.showEntries">
                                                <div class="log-entry-detail" v-for="entry in row.entries" :key="entry.id">
                                                    <div class="is-size-7 mt-2" v-if="getComponentOrActivity(entry)">
                                                        <i class="far fa-fw fa-location-dot mr-1"></i>{{ getComponentOrActivity(entry) }}
                                                        <span v-if="isStemEntry(entry)" class="tag is-success is-light is-size-7 ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                                                    </div>
                                                    <div class="is-size-7 has-text-grey is-flex is-align-items-center">
                                                        <span>
                                                            <i class="far fa-fw fa-clock mr-1"></i>{{ displayTimes(entry.start_date, entry.end_date) }} ({{ entry.mins }} active min)
                                                        </span>
                                                        <span class="ml-3 has-text-danger is-clickable delete-link" @click="confirmDeleteEntry(entry)" v-if="canDeleteEntry(entry)">
                                                            <i class="fas fa-fw fa-times mr-1"></i>Remove
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </td>
                                <td class="has-text-right" style="width: 10px;" v-if="row.entries.length === 1 && canDeleteEntry(row.entries[0])">
                                    <span class="row-action delete-x is-clickable" title="Remove" @click="confirmDeleteEntry(row.entries[0])">
                                        <i class="fas fa-times"></i>
                                    </span>
                                </td>
                                <td v-else-if="row.entries.length === 1"></td>
                            </template>
                        </tr>
                    </template>
                </table>

                    <hr/>

                    <div><i class="fad fa-clock fa-fw mr-2 has-text-success"></i><strong>{{totalTime}}</strong> Total Time Recorded</div>
                </template>
            </ui-card>
        </div>

        <!-- Study Log Entry Panel -->
        <study-log-entry-panel
            :is-open="showEntryPanel"
            :enrollment-id="enrollmentId"
            :objectives="objectives"
            :preselected-activity="preselectedActivity"
            :existing-entries="allEntries"
            :started-at="enrollment && enrollment.setup_approved_at"
            @close="closeEntryPanel"
            @added="onEntryAdded"
        />
    </div>
</template>

<script>
    import Vue from 'vue';
    import {client as http} from "../../http_client";
    import moment from "moment";
    import UiCard from "../../components/UiCard";
    import StudyLogEntryPanel from "../../components/course/StudyLogEntryPanel";
    import StudyActivityPanel from "../../components/studyActivity/StudyActivityPanel";
    import {mapActions, mapGetters} from "vuex";


    export default {
        components: {UiCard, StudyLogEntryPanel, StudyActivityPanel},
        props: ['enrollmentId'],
        data: function() {
            return {
                studyLog: [],
                showEntryPanel: false,
                preselectedActivity: null,
                isLoading: true
            };
        },
        computed: {
            ...mapGetters(['enrollment', 'milestoneLock']),
            milestoneLocked: function() {
                return this.milestoneLock.locked;
            },
            lockedMilestone: function() {
                return this.milestoneLock.milestone;
            },
            isReadonly: function() {
                // Completed enrollments are frozen server-side (API rejects new
                // entries and deletes), so present the log as read-only
                return !!this.enrollment?.completed_at;
            },
            requiresSetupApproval: function() {
                return this.enrollment &&
                       this.enrollment.course &&
                       this.enrollment.course.type === 'self_directed' &&
                       this.enrollment.setup_status !== 'approved';
            },
            canLogTime: function() {
                return !this.isReadonly && !this.requiresSetupApproval && !this.milestoneLocked;
            },
            totalTime: function() {
                // Duration, not time of day — moment's HH:mm wraps every 24 hours
                const total = this.studyLog.reduce((acc, log) => acc + (parseInt(log.totalmins, 10) || 0), 0);
                return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
            },
            // Get max hours from linked config
            maxHours: function() {
                if (!this.enrollment?.configs || this.enrollment.configs.length === 0) return 0;
                return this.enrollment.configs[0]?.max_hours || 0;
            },
            // Get total logged hours
            totalLoggedHours: function() {
                const totalMins = this.allEntries.reduce((sum, entry) => sum + (parseInt(entry.mins, 10) || 0), 0);
                return Math.round((totalMins / 60) * 100) / 100;
            },
            // Flatten all entries for conflict checking
            allEntries: function() {
                return this.studyLog.flatMap(log => log.entries);
            },
            // Get objectives from the enrollment's study plan
            objectives: function() {
                if (!this.enrollment?.setup_data) return [];

                const setupData = this.enrollment.setup_data;
                const objectives = [];

                // Get course config for objective text mapping
                let courseConfig = this.enrollment.course?.self_directed_config || {};
                if (typeof courseConfig === 'string') {
                    try {
                        courseConfig = JSON.parse(courseConfig);
                    } catch (e) {
                        courseConfig = {};
                    }
                }

                // Parse course objectives if available
                let courseObjectives = courseConfig.objectives || [];
                if (typeof courseObjectives === 'string') {
                    try {
                        courseObjectives = JSON.parse(courseObjectives);
                    } catch (e) {
                        courseObjectives = courseObjectives.split('\n').filter(o => o.trim());
                    }
                }

                // Map selected objectives to their text
                if (setupData.selected_objectives) {
                    setupData.selected_objectives.forEach(id => {
                        const obj = courseObjectives.find(o => (o.id || o) === id);
                        if (obj) {
                            objectives.push(obj.text || obj);
                        }
                    });
                }

                // Add custom objectives
                if (setupData.custom_objectives) {
                    setupData.custom_objectives.forEach(obj => {
                        if (obj && obj.trim()) {
                            objectives.push(obj);
                        }
                    });
                }

                return objectives;
            },
            // Get objectives with their progress (logged hours, planned hours, remaining)
            objectivesWithProgress: function() {
                if (!this.enrollment?.setup_data) return [];

                const setupData = this.enrollment.setup_data;
                const hourAllocations = setupData.hour_allocations || {};

                // Get course config for objective text mapping
                let courseConfig = this.enrollment.course?.self_directed_config || {};
                if (typeof courseConfig === 'string') {
                    try {
                        courseConfig = JSON.parse(courseConfig);
                    } catch (e) {
                        courseConfig = {};
                    }
                }

                // Parse course objectives if available
                let courseObjectives = courseConfig.objectives || [];
                if (typeof courseObjectives === 'string') {
                    try {
                        courseObjectives = JSON.parse(courseObjectives);
                    } catch (e) {
                        courseObjectives = courseObjectives.split('\n').filter(o => o.trim());
                    }
                }

                const result = [];

                // Map selected objectives
                if (setupData.selected_objectives) {
                    setupData.selected_objectives.forEach(id => {
                        const obj = courseObjectives.find(o => (o.id || o) === id);
                        if (obj) {
                            const text = obj.text || obj;
                            const plannedHours = hourAllocations[id] || 0;
                            const loggedMins = this.getLoggedMinutesForActivity(text);
                            const loggedHours = Math.round((loggedMins / 60) * 100) / 100;
                            result.push({
                                id,
                                text,
                                // predefined objectives only — custom objectives never
                                // qualify for STEM (same rule as the server's checkStem
                                // in setup.js)
                                stem: !!obj.stem,
                                plannedHours,
                                loggedHours,
                                remainingHours: Math.max(0, Math.round((plannedHours - loggedHours) * 100) / 100)
                            });
                        }
                    });
                }

                // Add custom objectives
                if (setupData.custom_objectives) {
                    setupData.custom_objectives.forEach((obj, index) => {
                        if (obj && obj.trim()) {
                            const id = 'custom_' + index;
                            const plannedHours = hourAllocations[id] || 0;
                            const loggedMins = this.getLoggedMinutesForActivity(obj);
                            const loggedHours = Math.round((loggedMins / 60) * 100) / 100;
                            result.push({
                                id,
                                text: obj,
                                plannedHours,
                                loggedHours,
                                remainingHours: Math.max(0, Math.round((plannedHours - loggedHours) * 100) / 100)
                            });
                        }
                    });
                }

                return result;
            }
        },
        methods: {
            ...mapActions(['getEnrollmentById', 'getMilestoneLock']),
            // Duration display, matching the Total Time Recorded footer — hours:minutes,
            // not decimal fractions of an hour
            formatDuration: function(hours) {
                const totalMins = Math.round(Math.max(0, hours) * 60);
                return `${String(Math.floor(totalMins / 60)).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`;
            },
            loadData: async function() {
                this.isLoading = true;
                this.studyLog = [];

                try {
                    // For new courses, enrollment includes study_log
                    await this.getEnrollmentById(this.enrollmentId);
                    this.logBuilder(this.enrollment.study_log || []);
                    await this.loadMilestoneLock();
                } catch (err) {
                    console.error('Error loading study log data:', err);
                } finally {
                    this.isLoading = false;
                }
            },
            loadMilestoneLock: async function() {
                if (this.enrollment?.course?.type !== 'self_directed') return;
                await this.getMilestoneLock(this.enrollmentId);
            },
            logBuilder: function(entries) {
                if (!entries || entries.length === 0) return;

                entries.forEach((entry) => {
                    let date = moment(entry.start_date).format('YYYY-MM-DD');
                    let existing = this.studyLog.find(l => l.date === date);
                    if (existing) {
                        existing.entries.push(entry);
                    } else {
                        this.studyLog.push({
                            date: date,
                            entries: [entry],
                            start_date: entry.start_date,
                            end_date: entry.end_date,
                            component_id: entry.component_id,
                            showEntries: false
                        });
                    }
                });

                this.studyLog.forEach((l) => {
                    l.totalmins = l.entries.reduce((acc, entry) => acc + (parseInt(entry.mins, 10) || 0), 0);
                });

                // Sort by date descending (most recent first)
                this.studyLog.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
            },
            openEntryPanel: function(activity = null) {
                this.preselectedActivity = activity;
                this.showEntryPanel = true;
            },
            closeEntryPanel: function() {
                this.showEntryPanel = false;
                this.preselectedActivity = null;
            },
            onEntryAdded: function() {
                this.closeEntryPanel();
                this.loadData();
            },
            canDeleteEntry: function(entry) {
                if (this.isReadonly) return false;
                // Manual entries ('m-') and classic-imported entries ('legacy:',
                // deletable in the legacy app) may be removed; modern auto-tracked
                // online time may not (mirrors the API rule)
                if (entry.session_id && !entry.session_id.startsWith('m-') && !entry.session_id.startsWith('legacy:')) {
                    return false;
                }
                return true;
            },
            confirmDeleteEntry: function(entry) {
                this.$buefy.dialog.confirm({
                    title: 'Remove Entry',
                    message: 'Are you sure you want to remove this study log entry? This cannot be undone.',
                    confirmText: 'Remove',
                    type: 'is-danger',
                    hasIcon: true,
                    onConfirm: () => this.deleteEntry(entry)
                });
            },
            deleteEntry: async function(entry) {
                try {
                    const endpoint = `app/enrollments/${this.enrollmentId}/studylog/${entry.id}`;
                    await http.delete(endpoint, {force: true});

                    this.$buefy.toast.open({
                        message: 'Entry removed successfully',
                        type: 'is-success'
                    });

                    this.loadData();
                } catch (err) {
                    console.error('Error deleting entry:', err);
                    this.$buefy.toast.open({
                        message: err.response?.data || 'Failed to remove entry',
                        type: 'is-danger'
                    });
                }
            },
            getComponent(id) {
                if (!this.enrollment?.course?.outline) return null;
                let component = null;
                this.enrollment.course.outline.forEach((section) => {
                    if (section.components) {
                        section.components.forEach((c) => {
                            if (c.component_id === id) {
                                component = c;
                            }
                        });
                    }
                });
                return component;
            },
            ucfirst(str) {
                if (!str) return str;
                return str.charAt(0).toUpperCase() + str.slice(1);
            },
            getLoggedMinutesForActivity(activityText) {
                // Sum minutes from all entries where activity matches (case-insensitive)
                const normalizedActivity = activityText.toLowerCase().trim();
                return this.allEntries
                    .filter(entry => entry.activity && entry.activity.toLowerCase().trim() === normalizedActivity)
                    .reduce((sum, entry) => sum + (parseInt(entry.mins, 10) || 0), 0);
            },
            getComponentOrActivity(entry) {
                // For manual entries with activity stored in session metadata
                if (entry.activity) {
                    return this.ucfirst(entry.activity);
                }
                // Try to get component title
                const component = this.getComponent(entry.component_id);
                return component ? component.title : null;
            },
            // Same attribution as the server (STEM_ACTIVITY_RE in
            // study_log_window.js / apply()'s award-split calc): students log
            // against objective text, and STEM objectives carry the word.
            isStemEntry(entry) {
                return !!(entry.activity && /(^|\W)STEM(\W|$)/.test(entry.activity));
            },
            displayDates: function(start, end) {
                let s = moment(start);
                let e = moment(end);
                if (s.isSame(e, 'day')) {
                    return s.format('dddd, MMMM Do, YYYY, h:mm A') + ' - ' + e.format('h:mm A');
                } else {
                    return s.format('MMM Do, YYYY, h:mm A') + ' - ' + e.format('MMM Do, YYYY, h:mm A');
                }
            },
            displayTimes: function(start, end) {
                let s = moment(start);
                let e = moment(end);
                if (s.isSame(e, 'day')) {
                    return s.format('h:mm A') + ' - ' + e.format('h:mm A');
                } else {
                    return s.format('MMM Do, YYYY, h:mm A') + ' - ' + e.format('MMM Do, YYYY, h:mm A');
                }
            }
        },
        watch: {
            // Watch for route changes (navigation without remount)
            enrollmentId: function(newId, oldId) {
                if (newId && newId !== oldId) {
                    this.loadData();
                }
            }
        },
        mounted() {
            this.loadData().then(() => {
                // Check if we should open the panel with a preselected activity
                const activity = this.$route.query.activity;
                if (activity) {
                    this.$nextTick(() => {
                        this.openEntryPanel(activity);
                    });
                }
            });
        }
    };
</script>

<style lang="scss" scoped>
    .objectives-section {
        h5 {
            color: #363636;
            font-weight: 600;
            margin-bottom: 1rem;
        }

        .objectives-content {
            padding-left: 1.1rem;
        }

        .objective-row {
            padding: 15px 10px;
            border-bottom: 1px solid #f0f0f0;

            &:last-of-type {
                border-bottom: none;
            }

            .objective-title {
                font-weight: 500;
                color: #363636;
                line-height: 1.4;
            }

            .hours-display {
                font-size: 0.85rem;
                color: #666;
                margin-top: 0.1rem;
                margin-bottom: 0;
            }

            ::v-deep .progress-wrapper {
                margin-top: 0.15rem;
                margin-bottom: 0;
            }

            .columns.mb-0 {
                margin-bottom: 0 !important;
            }
        }
    }

    .log-row {

        .log-title {
            margin-top: 2px;
            font-size: 1em;
            font-weight: bold;
        }

        .log-date {
            margin-top: 5px;
            font-size: 0.8em;
        }

        .log-entry-detail {
            padding: 8px 0;
            border-bottom: 1px solid #f0f0f0;

            &:last-child {
                border-bottom: none;
            }
        }

        .delete-link {
            opacity: 0.85;
            transition: opacity 0.2s, border-color 0.2s;
            border: 1px solid #dbdbdb;
            border-radius: 4px;
            padding: 0 0.4rem;

            &:hover {
                opacity: 1;
                border-color: #b5b5b5;
            }
        }

        td {
            border-color: #eaeaea;
            padding: 15px 10px;
        }

        &:last-child td {
            border: none;
        }
    }

    /* Lightweight row actions — shared look with the Notes and Files lists */
    .row-action {
        display: inline-block;
        font-size: 1.25rem;
        line-height: 1;
        color: #7a7a7a;
        padding: 0.3rem 0.45rem;
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        transition: color 0.2s, border-color 0.2s;

        &:hover {
            color: #363636;
            border-color: #b5b5b5;
        }

        &.delete-x {
            color: #f14668;

            &:hover {
                color: #cc0f35;
            }
        }
    }
</style>
